import { useMemo, useState } from "react";
import * as R from "ramda";
import { arrayOf, bool, number, shape, string } from "prop-types";
import NarrativeBodyChartValue from "./NarrativeBodyChartValue.jsx";
import NarrativeChart from "../NarrativeChart/NarrativeChart.jsx";
import useBoundingClientRect from "../../common/useBoundingClientRect.js";
import {
  getDataByLocation,
  getDataByDistribution,
  indicatorDataShape,
} from "../../../../Common/js/common/datacenterData.js";

const propTypes = {
  title: string.isRequired,
  source: string.isRequired,
  backgroundColor: string.isRequired,
  foregroundColor: string.isRequired,
  image: string.isRequired,
  dottedLineForMissingData: bool,
  excludeUrl: string,
  primary: shape({
    indicator: number.isRequired,
    title: string.isRequired,
    location: number.isRequired,
  }).isRequired,
  secondary: shape({
    indicator: number.isRequired,
    title: string.isRequired,
    location: number.isRequired,
  }),
  otherLines: shape({
    indicator: number.isRequired,
    // breakdown: oneOf(["location", "distribution"]).isRequired, not currently used (see otherDataByDistribution below)
  }),
  events: arrayOf(
    shape({
      start: string.isRequired, // year
      end: string, // could be null
      text: string.isRequired,
      href: string, // could be null
      label: string, // could be null
    }),
  ).isRequired,
  results: shape({
    data: arrayOf(indicatorDataShape).isRequired,
    errorMessage: string,
  }).isRequired,
};

const NarrativeBodyChart = (props) => {
  const {
    title,
    source,
    image,
    backgroundColor,
    foregroundColor,
    dottedLineForMissingData,
    events,
    primary,
    secondary,
    otherLines,
    excludeUrl,
  } = props;

  const { data } = props.results;

  const [{ width }, chartContainerRef] = useBoundingClientRect();

  // there should be only 1 location
  const primaryData = useMemo(() => {
    const primaryDataByLocation = R.compose(
      getDataByLocation,
      R.find(R.pathEq(["indicator", "id"], primary.indicator)),
    )(data);

    return primaryDataByLocation[primary.location];
  }, [data, primary.indicator, primary.location]);

  // start with the last year selected
  const lastYear = R.compose(
    R.prop("timeFrame"),
    R.last,
    R.prop("dataPoints"),
  )(primaryData);

  const [year, setYear] = useState(lastYear);

  // there should be only 1 location
  const secondaryData = useMemo(() => {
    if (!secondary) {
      return;
    }
    const secondaryDataByLocation = R.compose(
      getDataByLocation,
      R.find(R.pathEq(["indicator", "id"], secondary.indicator)),
    )(data);

    return secondaryDataByLocation[secondary.location];
  }, [data, secondary]);

  const primaryYearData = useMemo(() => {
    return R.compose(
      R.find(R.propEq("timeFrame", year)),
      R.prop("dataPoints"),
    )(primaryData);
  }, [primaryData, year]);

  const secondaryYearData = useMemo(() => {
    if (!secondaryData) {
      return;
    } else {
      return R.compose(
        R.find(R.propEq("timeFrame", year)),
        R.prop("dataPoints"),
      )(secondaryData);
    }
  }, [secondaryData, year]);

  const otherLinesData = useMemo(() => {
    if (!otherLines) {
      return;
    }

    // This is assuming that all NarrativeBodyChart's otherLines are by distribution
    // (not location)  If we need location in the future, this will need to be reworked
    const otherDataByDistribution = R.compose(
      R.values,
      getDataByDistribution,
      R.find(R.pathEq(["indicator", "id"], otherLines.indicator)),
    )(data);

    return otherDataByDistribution;
  }, [data, otherLines]);

  const otherLinesYearData = useMemo(() => {
    if (!otherLinesData) {
      return;
    }

    const getYearDataPoint = R.compose(
      R.find(R.propEq("timeFrame", year)),
      R.prop("dataPoints"),
    );

    return R.compose(
      R.map((lineData) => {
        return {
          ...lineData,
          yearDataPoint: getYearDataPoint(lineData),
        };
      }),
    )(otherLinesData);
  }, [otherLinesData, year]);

  return (
    <div
      className="narrative-body-chart container container--has-background"
      style={{
        backgroundColor: backgroundColor,
        color: foregroundColor,
        "--background-color": backgroundColor,
        "--foreground-color": foregroundColor,
      }}
    >
      <div className="container container--100">
        <div className="narrative-body-chart__title">{title}</div>
        {source && <div className="narrative-body-chart__source">{source}</div>}

        <div className="container--flex">
          <div className="container__col container__col--no-flex narrative-body-chart__info">
            <img src={image} alt="" className="narrative-body-chart__img" />
            <div className="narrative-body-chart__info-title">{year}</div>
            <NarrativeBodyChartValue
              title={primary.title}
              color="white"
              value={primaryYearData && primaryYearData.displayValue}
            />
            {secondary && (
              <NarrativeBodyChartValue
                title={secondary.title}
                value={secondaryYearData && secondaryYearData.displayValue}
              />
            )}

            {otherLinesYearData && (
              <div className="narrative-body-chart__breakdowns">
                {otherLinesYearData.map((lineData) => (
                  <div
                    key={lineData.id}
                    className="narrative-body-chart__breakdown"
                  >
                    <div className="narrative-body-chart__breakdown-value">
                      {(lineData.yearDataPoint &&
                        lineData.yearDataPoint.displayValue) ||
                        "-"}
                    </div>
                    <div className="narrative-body-chart__breadown-label">
                      {lineData.characteristic}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="container__col container__col--flex narrative-body-chart__chart"
            ref={chartContainerRef}
          >
            {width && (
              <NarrativeChart
                width={width}
                events={events}
                dottedLineForMissingData={dottedLineForMissingData}
                hoveredYear={year}
                onYearHover={setYear}
                primaryData={primaryData}
                secondaryData={secondaryData}
                otherLinesData={otherLinesData}
                excludeUrl={excludeUrl}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

NarrativeBodyChart.propTypes = propTypes;
export default NarrativeBodyChart;
